import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  XCircle,
  Loader2,
  AlertTriangle,
  Send,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

type DenyStatus = 'form' | 'loading' | 'success' | 'error';

export default function DenyCountRequest() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<DenyStatus>('form');
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No approval token provided');
    }
  }, [token]);

  const handleDeny = async () => {
    if (!token) return;
    setStatus('loading');
    try {
      const { error } = await supabase.rpc('deny_count_request', {
        p_approval_token: token,
        p_reason: reason.trim() || null,
      });

      if (error) throw error;
      setStatus('success');
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to deny request');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Form */}
        {status === 'form' && (
          <div className="rounded-xl border bg-card p-8 space-y-4">
            <div className="text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4">
                <XCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-semibold">Deny Count Request</h2>
              <p className="text-sm text-muted-foreground mt-1">
                The cashier will be notified that their request was denied.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Wrong shift, try again later"
              />
            </div>

            <Button
              onClick={handleDeny}
              variant="destructive"
              className="w-full"
              size="lg"
            >
              <Send className="h-4 w-4 mr-2" />
              Deny Request
            </Button>
          </div>
        )}

        {/* Loading */}
        {status === 'loading' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Processing...</p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mx-auto">
              <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold">Request Denied</h2>
            <p className="text-sm text-muted-foreground">
              The cashier has been notified. You can close this page.
            </p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mx-auto">
              <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold">Action Failed</h2>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
            <p className="text-xs text-muted-foreground">
              This link may have already been used, expired, or the request was already handled.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
